import {
  Account,
  AccountType,
  AccountWithRealBalance,
  AnalysisCalendar,
  CashFlow,
  CofidisDebtCollectionIndicatorValues,
  CofidisDebtConsolidationIndicatorValues,
  CreditInsights,
  DebtCollectionIndicatorValues,
  DebtCollectionSynthesis,
  DebtConsolidationIndicatorValues,
  DebtConsolidationLoanAnalysis,
  Transaction,
  TransactionBankOperationCategory,
  TransactionCategory,
  TransactionCreditCategory,
} from '@algoan/dashboard/model';
import { getTotalAmount, getRealTotalBalance } from '@algoan/dashboard/utils';
import { convertFrequentTransactions } from './bank-statements-formatters';
import {
  calculateIfBankAccountSeizureEventOccurred,
  calculateIfThirdPartyHolderNoticeEventOccurred,
  calculateInternationalTransfersIndicator,
  calculateOverdraftIndicatorValue,
  computeDangerCashFlowIndicatorValues,
  computeDifferedPaymentIndicator,
  computeExplicitIndicatorValues,
  computePaymentMethods,
  calculateBankTransactionsToBeJustifiedIndicatorValue,
  calculateInternalTransfersWithoutRelatedTransactions,
} from '@algoan/dashboard/calculators';

const loanRepaymentCashflows = [
  TransactionCreditCategory.LOAN_REPAYMENT,
  TransactionCreditCategory.LOAN_REPAYMENT_PERSONAL,
  TransactionCreditCategory.LOAN_REPAYMENT_REAL_ESTATE,
  TransactionCreditCategory.LOAN_REPAYMENT_REVOLVING,
  TransactionCreditCategory.LOAN_REPAYMENT_FAST,
  TransactionCreditCategory.LOAN_REPAYMENT_STUDENT,
] as TransactionCategory[];

export const formatDebtCollectionIndicatorValues = (
  creditInsights: CreditInsights,
  transactions?: Transaction[],
): DebtCollectionIndicatorValues => {
  const didThirdPartyHolderNoticeEventOccur = calculateIfThirdPartyHolderNoticeEventOccurred(
    creditInsights.risks.heavyFailures,
  );
  const didBankAccountSeizureEventOccur = calculateIfBankAccountSeizureEventOccurred(
    creditInsights.risks.heavyFailures,
  );

  const overdraftIndicatorValue = calculateOverdraftIndicatorValue(creditInsights.risks.overdraft);

  const dangerCashFlowIndicatorValues = computeDangerCashFlowIndicatorValues(creditInsights.cashFlows);

  const internationalTransfers = calculateInternationalTransfersIndicator(transactions);

  const bankTransactionsToBeJustified = calculateBankTransactionsToBeJustifiedIndicatorValue(transactions);

  const internalTransfersWithoutRelatedTransactions =
    calculateInternalTransfersWithoutRelatedTransactions(transactions);

  return {
    dangerCashFlowIndicatorValues,
    didBankAccountSeizureEventOccur,
    didThirdPartyHolderNoticeEventOccur,
    overdraftIndicatorValue,
    internationalTransfers,
    bankTransactionsToBeJustified,
    internalTransfersWithoutRelatedTransactions,
  };
};

export const formatCofidisDebtCollectionIndicatorValues = (
  creditInsights: CreditInsights,
  transactions?: Transaction[],
): CofidisDebtCollectionIndicatorValues => {
  return formatDebtCollectionIndicatorValues(creditInsights, transactions);
};

export const formatDebtConsolidationIndicatorValues = (
  creditInsights: CreditInsights,
  transactions?: Transaction[],
): DebtConsolidationIndicatorValues => {
  return formatDebtCollectionIndicatorValues(creditInsights, transactions);
};

export const formatCofidisDebtConsolidationIndicatorValues = (
  creditInsights: CreditInsights,
  historicalDepth: number,
  transactions: Transaction[],
  accounts: Account[],
  analysisDate: string,
): CofidisDebtConsolidationIndicatorValues => {
  const explicitIndicatorValues = computeExplicitIndicatorValues(
    creditInsights,
    historicalDepth,
    transactions,
    accounts,
    analysisDate,
  );

  return { ...formatDebtCollectionIndicatorValues(creditInsights, transactions), explicitIndicatorValues };
};

export const formatDebtCollectionSynthesis = (
  accounts: AccountWithRealBalance[],
  cashFlows: CashFlow[],
): Omit<DebtCollectionSynthesis, 'cashFlowIndicatorValues'> => {
  const totalBalance = getRealTotalBalance(
    accounts.filter((account) => account.type === AccountType.CHECKING || account.type === AccountType.SAVINGS),
  );

  const totalUnpaidDirectDebit = getTotalAmount(
    cashFlows.filter((cashFlow) => cashFlow.category === TransactionBankOperationCategory.REJECTION_PAYMENT),
  );

  const loanNumber = cashFlows.filter((cashFlow) => loanRepaymentCashflows.includes(cashFlow.category)).length;

  const totalLocDrawdown = getTotalAmount(
    cashFlows.filter((cashFlow) => cashFlow.category === TransactionCreditCategory.LOAN_DRAWDOWN),
  );

  const savingsAccounts = accounts.filter((account) => account.type === AccountType.SAVINGS);

  return {
    lastSavingsBalance: getRealTotalBalance(savingsAccounts),
    totalBalance,
    totalUnpaidDirectDebit,
    loanNumber,
    totalLocDrawdown,
  };
};

export const formatDebtConsolidationSynthesis = (
  accounts: AccountWithRealBalance[],
  cashFlows: CashFlow[],
  creditInsights: CreditInsights,
  transactions: Transaction[],
  minimumNumberOfTransactionsToDisplayFrequentTransactions: number,
  calendar: AnalysisCalendar[],
): Omit<DebtConsolidationLoanAnalysis, 'cashFlowIndicatorValues'> => {
  const checkingsAccounts = accounts.filter((account) => account.type === AccountType.CHECKING);
  const savingsAccounts = accounts.filter((account) => account.type === AccountType.SAVINGS);
  const loanCashFlows = cashFlows.filter((cashFlow) => loanRepaymentCashflows.includes(cashFlow.category));

  const frequentTransactions = convertFrequentTransactions(
    transactions,
    minimumNumberOfTransactionsToDisplayFrequentTransactions,
    creditInsights?.expenses.frequentTransactions,
  );

  const paymentMethods = computePaymentMethods(transactions);

  const totalAmountOfLoanRepayment = getTotalAmount(
    cashFlows.filter((cashFlow) => cashFlow.category === TransactionCreditCategory.LOAN_REPAYMENT),
  );

  const totalAmountOfLoanRepaymentRealEstate = getTotalAmount(
    cashFlows.filter((cashFlow) => cashFlow.category === TransactionCreditCategory.LOAN_REPAYMENT_REAL_ESTATE),
  );

  const totalAmountOfLoanRepaymentPersonal = getTotalAmount(
    cashFlows.filter((cashFlow) => cashFlow.category === TransactionCreditCategory.LOAN_REPAYMENT_PERSONAL),
  );

  const totalAmountOfLoanRepaymentRevolving = getTotalAmount(
    cashFlows.filter((cashFlow) => cashFlow.category === TransactionCreditCategory.LOAN_REPAYMENT_REVOLVING),
  );

  const totalAmountOfLoanRepaymentFast = getTotalAmount(
    cashFlows.filter((cashFlow) => cashFlow.category === TransactionCreditCategory.LOAN_REPAYMENT_FAST),
  );

  const totalAmountOfLoanRepaymentStudent = getTotalAmount(
    cashFlows.filter((cashFlow) => cashFlow.category === TransactionCreditCategory.LOAN_REPAYMENT_STUDENT),
  );

  const meanMonthlyAmountOfLoanCategories =
    Math.abs(
      totalAmountOfLoanRepayment +
        totalAmountOfLoanRepaymentRealEstate +
        totalAmountOfLoanRepaymentPersonal +
        totalAmountOfLoanRepaymentRevolving +
        totalAmountOfLoanRepaymentFast +
        totalAmountOfLoanRepaymentStudent,
    ) / calendar.length;

  const paymentIndicatorValues = {
    differedPaymentIndicator: computeDifferedPaymentIndicator(creditInsights.cashFlows),
  };

  return {
    totalBalance: getRealTotalBalance(checkingsAccounts.concat(savingsAccounts)),
    lastSavingsBalance: getRealTotalBalance(savingsAccounts),
    lastMonthCashburn: 0, // not implemented yet
    checkingsAccountNumber: checkingsAccounts.length,
    savingsAccountNumber: savingsAccounts.length,
    loansNumber: loanCashFlows.length,
    totalMonthlyAmount: meanMonthlyAmountOfLoanCategories,
    frequentTransactions,
    paymentMethods,
    paymentIndicatorValues,
  };
};
